import type { SQLiteDatabase } from 'expo-sqlite';

import type { ExpenseRecurring } from '@/db/types';
import { parseLocalDate } from '@/lib/dates';
import { addExpense, type NewExpenseInput } from '@/lib/expenses';

/**
 * Bulk import for expenses. Same tolerant-mapper pattern as inventory.
 *
 * Required: title (or description), amount. Optional: category, vendor,
 * paid date, recurring (monthly/quarterly/biannual/annual), tax
 * deductible (yes/no), notes.
 */

export interface ExpensesCsvImportResult {
  inserted: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

const COLUMN_ALIASES: Record<keyof RawRow, string[]> = {
  title: ['title', 'description', 'item', 'memo'],
  category: ['category', 'type'],
  vendor: ['vendor', 'payee', 'merchant', 'paid to'],
  amount: ['amount', 'cost', 'price', 'total', 'paid'],
  paid_on: ['paid on', 'date', 'paid', 'paid date'],
  recurring: ['recurring', 'frequency', 'repeat'],
  tax_deductible: ['tax deductible', 'deductible', 'tax'],
  notes: ['notes', 'comments', 'memo notes'],
};

interface RawRow {
  title?: string;
  category?: string;
  vendor?: string;
  amount?: string;
  paid_on?: string;
  recurring?: string;
  tax_deductible?: string;
  notes?: string;
}

export interface ExpensesCsvPreviewRow {
  index: number;
  ok: boolean;
  reason?: string;
  parsed?: Omit<NewExpenseInput, 'propertyId'>;
}

export function parseExpensesCsv(text: string): ExpensesCsvPreviewRow[] {
  const rows = parseCsv(text);
  if (rows.length === 0) return [];
  const header = rows[0].map((c) => normalizeKey(c));
  const colMap = mapColumns(header);

  const out: ExpensesCsvPreviewRow[] = [];
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i];
    if (cells.length === 0 || cells.every((c) => c.trim() === '')) continue;
    const raw: RawRow = {};
    for (const [key, idx] of Object.entries(colMap)) {
      if (idx == null) continue;
      raw[key as keyof RawRow] = cells[idx]?.trim();
    }
    const title = raw.title?.trim();
    const cents = parseCents(raw.amount);
    if (!title) { out.push({ index: i, ok: false, reason: 'Missing title' }); continue; }
    if (cents == null || cents <= 0) {
      out.push({ index: i, ok: false, reason: 'Missing or invalid amount' });
      continue;
    }
    out.push({
      index: i,
      ok: true,
      parsed: {
        category: raw.category?.trim() || 'Other',
        vendor: raw.vendor?.trim() || null,
        amount_cents: cents,
        paid_on: parseDate(raw.paid_on) ?? Date.now(),
        description: title,
        recurring: parseRecurring(raw.recurring),
        tax_deductible: parseBool(raw.tax_deductible) ? 1 : 0,
        notes: raw.notes?.trim() || null,
      },
    });
  }
  return out;
}

export async function importExpensesCsv(
  db: SQLiteDatabase,
  propertyId: string,
  rows: ExpensesCsvPreviewRow[]
): Promise<ExpensesCsvImportResult> {
  const result: ExpensesCsvImportResult = { inserted: 0, skipped: 0, errors: [] };
  await db.withTransactionAsync(async () => {
    for (const r of rows) {
      if (!r.ok || !r.parsed) {
        result.skipped += 1;
        if (r.reason) result.errors.push({ row: r.index, message: r.reason });
        continue;
      }
      try {
        await addExpense(db, { ...r.parsed, propertyId });
        result.inserted += 1;
      } catch (err) {
        result.skipped += 1;
        result.errors.push({
          row: r.index,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  });
  return result;
}

// --- helpers --------------------------------------------------------------

function normalizeKey(s: string): string {
  return s.toLowerCase().replace(/[\s_\-]+/g, ' ').trim();
}

function mapColumns(header: string[]): Partial<Record<keyof RawRow, number>> {
  const out: Partial<Record<keyof RawRow, number>> = {};
  for (const [key, aliases] of Object.entries(COLUMN_ALIASES) as [keyof RawRow, string[]][]) {
    for (const alias of aliases) {
      const idx = header.indexOf(alias);
      if (idx >= 0) { out[key] = idx; break; }
    }
  }
  return out;
}

function parseDate(raw: string | undefined): number | null {
  if (!raw) return null;
  const t = parseLocalDate(raw);
  return isNaN(t) ? null : t;
}

function parseCents(raw: string | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : Math.round(n * 100);
}

function parseRecurring(raw: string | undefined): ExpenseRecurring | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v.startsWith('month')) return 'monthly';
  if (v.startsWith('quart')) return 'quarterly';
  if (v.startsWith('bi') || v.startsWith('semi')) return 'biannual';
  if (v.startsWith('annual') || v === 'yearly' || v === 'year') return 'annual';
  return null;
}

function parseBool(raw: string | undefined): boolean {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1' || v === 'y';
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      cell += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { row.push(cell); cell = ''; i += 1; continue; }
    if (c === '\r') { i += 1; continue; }
    if (c === '\n') {
      row.push(cell); cell = '';
      rows.push(row); row = [];
      i += 1; continue;
    }
    cell += c; i += 1;
  }
  if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
}
